import { Link } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Lock, X } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";

const GALLERY = [
  { src: "/kronix/key-art.png", caption: "The party faces the temporal golem" },
  { src: "/kronix/key-art-2.png", caption: "The dormant guardian of the ruined arena" },
  { src: "/kronix/key-art-3.png", caption: "Core strike — first blood on the construct" },
];

const INTEL = [
  {
    code: "INTEL 01",
    title: "The Combat",
    body: "Exaggerated weapons, vibrant particle trails, expressive poses — combat built to be read at a glance and felt in the hands.",
  },
  {
    code: "INTEL 02",
    title: "The Arena",
    body: "Ancient ruins, temporal constructs and a golem that glows like a dying star. The world is older than the war.",
  },
  {
    code: "INTEL 03",
    title: "The Heroes",
    body: "Stylized, chibi-fantasy and fast. Classes, roles and the roster remain classified until first transmission.",
  },
];

const Kronix = () => {
  const [lightbox, setLightbox] = useState(null);
  return (
  <div>
    <section data-testid="kronix-hero" className="relative flex min-h-[92vh] items-end overflow-hidden">
      <motion.div
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
      >
        <img src="/kronix/key-art.png" alt="KRONIX key art — heroes facing the temporal golem" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/35 to-[#0A0B0D]/60" />
      </motion.div>
      <EmberCanvas density={18} className="absolute inset-0 h-full w-full" />
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-16 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <Link
            to="/games"
            data-testid="kronix-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
          >
            <ArrowLeft size={14} /> THE VAULT
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
            <span className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">CONCEPT PHASE</span>
          </div>
          <h1 className="mt-4 font-display text-7xl font-black uppercase leading-none tracking-tight text-white sm:text-8xl lg:text-[11rem]">
            Kronix
          </h1>
          <p className="mt-6 max-w-2xl font-display text-xl font-semibold uppercase leading-snug tracking-wide text-[#C6CAD2] sm:text-2xl">
            High-energy isometric action MMORPG. Ancient arenas. Temporal constructs. Exaggerated combat.
          </p>
        </motion.div>
      </div>
    </section>

    <section className="border-y border-[#282D37] bg-[#0D0F12]">
      <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px lg:grid-cols-4">
        {[
          ["STATUS", "CONCEPT PHASE"],
          ["GENRE", "ISOMETRIC ACTION MMORPG"],
          ["STUDIO", "ARSENAL FORGE"],
          ["REVEAL", "FIRST TRANSMISSION TBA"],
        ].map(([k, v], i) => (
          <Reveal key={k} delay={0.08 * i}>
            <div data-testid={`kronix-meta-${i}`} className="px-6 py-8 text-center lg:px-8">
              <p className="font-mono text-[10px] tracking-[0.35em] text-[#5F6573]">{k}</p>
              <p className={`mt-2 font-mono text-xs font-bold tracking-widest sm:text-sm ${i === 0 ? "text-[#E52E2E]" : "text-[#F4F5F7]"}`}>
                {v}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="kronix-intel" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">TRANSMISSION INTERCEPTED</p>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
          Everything else is still in the fire
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {INTEL.map((b, i) => (
          <Reveal key={b.code} delay={0.1 * i}>
            <div
              data-testid={`kronix-intel-${i}`}
              className="group steel-panel relative h-full p-8 transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/50 hover:shadow-[0_0_40px_rgba(229,46,46,0.08)]"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm tracking-[0.3em] text-[#5F6573] transition-colors duration-500 group-hover:text-[#E52E2E]">
                  {b.code}
                </span>
                <Lock size={13} className="text-[#3A404E]" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7]">{b.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">{b.body}</p>
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-12 border border-dashed border-[#282D37] px-6 py-5 text-center font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">
          ROSTER · CLASSES · RELEASE WINDOW — CLASSIFIED // AWAITING FIRST TRANSMISSION
        </p>
      </Reveal>
    </section>

    <section data-testid="kronix-gallery" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">CONCEPT ART // FIRST LIGHT</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            First light from the arena
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={0.08 * i}>
              <button
                data-testid={`kronix-art-${i}`}
                onClick={() => setLightbox(i)}
                className="group relative block w-full cursor-zoom-in overflow-hidden border border-[#282D37]"
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  className="aspect-[3/2] w-full object-cover opacity-90 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-100"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0B0D]/90 to-transparent p-4 text-left">
                  <p className="font-mono text-[9px] tracking-[0.25em] text-[#9EA4B0]">{g.caption.toUpperCase()}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <AnimatePresence>
      {lightbox !== null && (
        <motion.div
          data-testid="kronix-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[90] flex cursor-zoom-out items-center justify-center bg-[#0A0B0D]/95 p-6 backdrop-blur-md"
        >
          <button
            data-testid="kronix-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute right-6 top-6 text-[#9EA4B0] transition-colors hover:text-[#E52E2E]"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <motion.figure
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-6xl"
          >
            <img
              src={GALLERY[lightbox].src}
              alt={GALLERY[lightbox].caption}
              className="max-h-[80vh] w-full border border-[#282D37] object-contain"
            />
            <figcaption className="mt-4 text-center font-mono text-xs tracking-[0.3em] text-[#9EA4B0]">
              {GALLERY[lightbox].caption.toUpperCase()}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>

    <section data-testid="kronix-cta" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 text-center lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FOLLOW DEVELOPMENT</p>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            First transmissions land in the <span className="text-[#E52E2E]">Forge Journal</span>
          </h2>
          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/journal"
              data-testid="kronix-cta-journal"
              className="group flex items-center gap-3 bg-[#E52E2E] px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(229,46,46,0.25)] transition-colors duration-300 hover:bg-[#FF3B3B]"
            >
              Read the Journal
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/games/territoria"
              data-testid="kronix-cta-territoria"
              className="border border-[#3A404E] px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
            >
              Explore Territoria
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
  );
};

export default Kronix;
